// Semantic (position-dependent) validation for RSchema payloads, layered on
// top of the format checks in validate.ts.
//
//   create        - format + every creator's keyId matches its public key
//   schema-update - format + signature by one of the creators + per-rule
//                   applicability against the resolved schema at the entry's
//                   parent frontier `at`. Rules within one update apply
//                   sequentially: later rules see the effect of earlier ones.
//
// Applicability is checked at `at` only: slot conflicts across forks (e.g.
// concurrent add-table of the same name on two branches) are NOT validity
// errors — they merge by per-slot LWW at resolution time.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { validationFailure, validationOk, } from "@hyper-hyper-space/hhs3_mvt";
import { verifyPayloadSignature, deserializePublicKeyFromBase64, computeKeyId } from "@hyper-hyper-space/hhs3_mvt";
import { validateRSchemaPayloadFormat } from "./validate.js";
import { collectExistsAtoms, collectRowFieldRefs, checkPredicateColumns, isValidSchemaName } from "./validate.js";
import { splitTableRef } from "./payload.js";
export function validateRSchemaPayload(payload, context) {
    return __awaiter(this, void 0, void 0, function* () {
        const formatResult = validateRSchemaPayloadFormat(payload);
        if (!formatResult.valid)
            return formatResult;
        if (context.mode === 'create') {
            return validateCreate(payload, context.ctx);
        }
        return validateUpdate(payload, context.schema, context.at);
    });
}
function validateCreate(create, ctx) {
    if (create.action !== 'create')
        return validationFailure("RSchema creation action must be 'create'");
    if (!isValidSchemaName(create.name))
        return validationFailure(`invalid schema name '${create.name}'`);
    const hashSuite = ctx.getHashSuite();
    const seen = new Set();
    for (const creator of create.creators) {
        if (seen.has(creator.keyId))
            return validationFailure(`duplicate schema creator '${creator.keyId}'`);
        seen.add(creator.keyId);
        try {
            const pk = deserializePublicKeyFromBase64(creator.publicKey);
            if (computeKeyId(pk, hashSuite) !== creator.keyId) {
                return validationFailure(`schema creator keyId '${creator.keyId}' does not match public key`);
            }
        }
        catch (_a) {
            return validationFailure(`schema creator '${creator.keyId}' public key is invalid`);
        }
    }
    return validationOk();
}
function creatorKeyLookup(creators) {
    return (keyId) => __awaiter(this, void 0, void 0, function* () {
        const creator = creators.find((c) => c.keyId === keyId);
        if (creator === undefined)
            return undefined;
        try {
            return deserializePublicKeyFromBase64(creator.publicKey);
        }
        catch (_a) {
            return undefined;
        }
    });
}
function validateUpdate(update, schema, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (update.action !== 'schema-update')
            return validationFailure("RSchema op action must be 'schema-update'");
        const view = yield schema.getView(at, at);
        if (!view.isCreator(update.author))
            return validationFailure(`schema-update author '${update.author}' is not a creator`);
        if (!(yield verifyPayloadSignature(update, creatorKeyLookup(view.getCreators())))) {
            return validationFailure(`schema-update signature from '${update.author}' could not be verified`);
        }
        return rulesApplicableAt(update.migration, view)
            ? validationOk()
            : validationFailure("schema-update migration is not applicable at this version");
    });
}
// Per-rule applicability, applied sequentially over a working copy of the
// resolved tables at `at`. Exported for the schema-update author path.
export function rulesApplicableAt(rules, view) {
    const tables = new Map();
    for (const name of view.getTableNames()) {
        tables.set(name, view.getTable(name));
    }
    for (const rule of rules) {
        if (!applyRule(tables, rule))
            return false;
    }
    return true;
}
// `where` fields of local exists atoms must be pub columns of the target
// (foreign targets are checked at group binding time, as for create).
function checkLocalPredicateTargets(def, tables) {
    var _a, _b, _c, _d;
    for (const target of Object.values((_a = def.fks) !== null && _a !== void 0 ? _a : {})) {
        const [group, table] = splitTableRef(target);
        if (group === undefined && !tables.has(table))
            return false;
    }
    for (const restriction of (_b = def.restrictions) !== null && _b !== void 0 ? _b : []) {
        for (const atom of collectExistsAtoms(restriction.rule)) {
            const [group, table] = splitTableRef(atom.table);
            if (group !== undefined)
                continue;
            const target = tables.get(table);
            if (target === undefined)
                return false;
            for (const field of Object.keys((_c = atom.where) !== null && _c !== void 0 ? _c : {})) {
                const column = target.columns[field];
                if (column === undefined || !((_d = column.pub) !== null && _d !== void 0 ? _d : false))
                    return false;
            }
        }
        if (checkPredicateColumns(def, restriction.rule, (t) => tables.get(t)) !== undefined)
            return false;
    }
    return true;
}
// Checks one rule against the working table set and applies it. Returns
// false if the rule is not applicable.
function applyRule(tables, rule) {
    var _a, _b, _c, _d, _e, _f;
    switch (rule.rule) {
        case 'add-table': {
            if (tables.has(rule.def.name))
                return false;
            const withNew = new Map(tables);
            withNew.set(rule.def.name, rule.def);
            if (!checkLocalPredicateTargets(rule.def, withNew))
                return false;
            tables.set(rule.def.name, rule.def);
            return true;
        }
        case 'drop-table': {
            if (!tables.has(rule.table))
                return false;
            // best-effort durability: refuse to drop a table still referenced
            // by another table's local FK or exists atom. This is an `at`-only
            // check (per-slot LWW merges can still produce a dangling local
            // target on another branch — a later FK write against it is then
            // voided at-use, an exists over it is false).
            for (const [name, def] of tables) {
                if (name === rule.table)
                    continue;
                for (const target of Object.values((_a = def.fks) !== null && _a !== void 0 ? _a : {})) {
                    const [group, t] = splitTableRef(target);
                    if (group === undefined && t === rule.table)
                        return false;
                }
                for (const restriction of (_b = def.restrictions) !== null && _b !== void 0 ? _b : []) {
                    for (const atom of collectExistsAtoms(restriction.rule)) {
                        const [group, t] = splitTableRef(atom.table);
                        if (group === undefined && t === rule.table)
                            return false;
                    }
                }
            }
            tables.delete(rule.table);
            return true;
        }
        case 'add-column': {
            const def = tables.get(rule.table);
            if (def === undefined)
                return false;
            if (def.columns[rule.column] !== undefined)
                return false;
            tables.set(rule.table, Object.assign(Object.assign({}, def), { columns: Object.assign(Object.assign({}, def.columns), { [rule.column]: rule.def }) }));
            return true;
        }
        case 'drop-column': {
            const def = tables.get(rule.table);
            if (def === undefined)
                return false;
            if (def.columns[rule.column] === undefined)
                return false;
            const columns = Object.assign({}, def.columns);
            delete columns[rule.column];
            if (Object.keys(columns).length === 0)
                return false; // a table needs at least one column
            if (((_c = def.fks) !== null && _c !== void 0 ? _c : {})[rule.column] !== undefined)
                return false; // drop the FK first (set-fks)
            // best-effort durability: refuse if any exists atom (local target
            // = this table) still references the column as a where-field
            for (const other of tables.values()) {
                for (const restriction of (_d = other.restrictions) !== null && _d !== void 0 ? _d : []) {
                    for (const atom of collectExistsAtoms(restriction.rule)) {
                        const [group, t] = splitTableRef(atom.table);
                        if (group === undefined && t === rule.table && ((_e = atom.where) !== null && _e !== void 0 ? _e : {})[rule.column] !== undefined)
                            return false;
                    }
                }
            }
            // refuse if THIS table's own restrictions still reference the column
            // as a subject-row field ($row.<col>, in cmp/str operands or as an
            // exists where-value)
            for (const restriction of (_f = def.restrictions) !== null && _f !== void 0 ? _f : []) {
                if (collectRowFieldRefs(restriction.rule).has(rule.column))
                    return false;
            }
            tables.set(rule.table, Object.assign(Object.assign({}, def), { columns }));
            return true;
        }
        case 'set-concurrent-deletes': {
            const def = tables.get(rule.table);
            if (def === undefined)
                return false;
            tables.set(rule.table, Object.assign(Object.assign({}, def), { concurrentDeletes: rule.value }));
            return true;
        }
        case 'set-fks': {
            const def = tables.get(rule.table);
            if (def === undefined)
                return false;
            for (const [column, target] of Object.entries(rule.fks)) {
                if (def.columns[column] === undefined)
                    return false;
                const [group, table] = splitTableRef(target);
                if (group === undefined && !tables.has(table))
                    return false;
            }
            tables.set(rule.table, Object.assign(Object.assign({}, def), { fks: rule.fks }));
            return true;
        }
        case 'set-restrictions': {
            const def = tables.get(rule.table);
            if (def === undefined)
                return false;
            const updated = Object.assign(Object.assign({}, def), { restrictions: rule.restrictions });
            if (!checkLocalPredicateTargets(updated, tables))
                return false;
            tables.set(rule.table, updated);
            return true;
        }
    }
}
